import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { Text } from '@chakra-ui/react'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { MinimumAreaBoundary } from '@editor/interfaces/diagram'
import { EditorConfig } from '@editor/interfaces/editor'
import {
  GridOrderDirection,
  GridType,
  InspectionItemGrid,
  IntervalsConfig,
} from '@editor/interfaces/inspectionItemGrid'
import { Editor, InspectionItem, ShapeKeyType } from '@editor/interfaces/interfaces'
import { PlaneSide, Polygon } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { setHidePlaneLabel } from '@editor/pages/projects/editor/store/editor'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { estimateGridDepth } from '@editor/services/GridDepthEstimation'
import { alignMinimumAreaBoundary, minAreaRectangleOfPolygon } from '@editor/services/MinimumRectangle'
import { findObjectsOfShapeIds } from '@editor/services/Object3D'
import { meterToMillimeter, withPromiseContained } from '@editor/services/Util'
import { getVolumeEstimationItem, pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { addWorkingTool, removeWorkingTool, setSelectedElementIds } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { cloneDeep } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { Vector3 } from 'three'

import {
  GRID_EDGE_DEFAULT_EDGE_DISTANCE,
  GRID_EDGE_DEFAULT_POINT,
  GRID_EDGE_MINIMUM_POINT,
  GRID_POINT_MINIMUM_INTERVAL,
} from '../constants'
import { applyGridIntervalPrefs, clearGridIntervalPrefs } from '../gridIntervalPrefs'
import {
  reset,
  resetPerVolumeId,
  resetWorking as resetWorkingGrid,
  setGrids,
  setIsLoading,
  setSelectedVolumeId,
  updateInterval,
  updateWorkingGridPoints,
} from '../store'
import {
  calculateMaxPointCount,
  generateGridPoints,
  mergeIntervalsWithSavedConfig,
  validateIntervalConfig,
} from '../utils'

/**
 * Editor hook for Grid tool
 */
const useEditor = ({ selectedTool, sceneRef, changeIsJobRunning, changeTool }: Editor): EditorConfig => {
  // Toast
  const successToastIdRef = useRef<string>(undefined)
  const infoToastIdRef = useRef<string>(undefined)

  // URL Params
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Hooks
  const { fetchInspectionItems } = useInspectionItems()

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const isLoading = useSelector((state: RootState) => state.toolGrid.isLoading)
  const inspectionSheet = useSelector((state: RootState) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const grids = useSelector((state: RootState) => state.toolGrid.grids)
  const intervals = useSelector((state: RootState) => state.toolGrid.intervals)
  const workingGridPoints = useSelector((state: RootState) => state.toolGrid.workingGridPoints)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Flags and vars
  const isDirty = Object.keys(intervals).length > 0
  const isSaveable = isDirty && !isLoading
  const isToolSelected = selectedTool === EDITOR_TOOLS.GRID
  const selectedPlanes = useMemo(
    () => selectedElementIds.filter((id) => shapes.polygons.find((plane) => plane.shape_id === id)),
    [selectedElementIds, shapes.polygons],
  )
  const isPlaneSelected = selectedPlanes.length > 0
  const selectedVolumeItem = useMemo(
    () => (isPlaneSelected ? getVolumeEstimationItem(inspectionItems, selectedPlanes[0]) : undefined),
    [inspectionItems, selectedPlanes, isPlaneSelected],
  ) // any of the selected shape is fine

  /**
   * If any of the volumes was deleted, reset its working grid
   */
  useEffect(
    () => {
      const volumeIdsToDelete = Object.keys(intervals).filter((volumeId) =>
        inspectionItems.every((item) => item.inspection_item_id !== volumeId),
      )
      volumeIdsToDelete.forEach((volumeId) => {
        dispatch(resetPerVolumeId(volumeId))
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspectionItems],
  )

  /**
   * Set grid data once inspection items are loaded
   */
  useEffect(() => {
    dispatch(
      setGrids(
        inspectionItems
          .filter((item) => item.grid)
          .map((item, itemIndex) => {
            const updatedItem = cloneDeep(item)
            if (updatedItem.grid) {
              updatedItem.grid.list_distances = updatedItem.grid.list_distances.map((distance, distanceIndex) => ({
                ...distance,
                name: `t${itemIndex + 1}-${distanceIndex + 1}`,
              }))
            }
            return updatedItem
          }),
      ),
    )
  }, [inspectionItems, dispatch])

  const checkIsLocked = useCallback(
    (grid: InspectionItemGrid | undefined, interval: IntervalsConfig, topPlaneId: string): boolean => {
      // If there's no existing grid, we're creating new one so no need to lock it.
      if (!grid || !sceneRef.current) return false

      // Grid without intervals are based on old algo, always lock them.
      if (!grid.intervals) return true

      // Can't identify if they should or not without mesh so always lock if grid already exists
      const mesh = findObjectsOfShapeIds(sceneRef.current, [topPlaneId])[0]
      if (grid && mesh) {
        const generatedGridPoints = generateGridPoints(interval).map((point) => point.map((v) => Math.round(v / 0.5)))
        const existingPositions = grid.list_distances
          // Convert world positions to local positions
          .map((distance) =>
            mesh
              .worldToLocal(new Vector3(...distance.position_on_grid_point))
              .toArray()
              .map((v) => Math.round(meterToMillimeter(v) / 0.5)),
          )

        // For every single existing positions, check if there's an equivalent in generated points
        // If there's none, it means the grid has been modified, so lock it.
        return !existingPositions.every((position) =>
          generatedGridPoints.some((point) => point[0] === position[0] && point[1] === position[1]),
        )
      }

      return false
    },
    [sceneRef],
  )

  const minimumRectangleBoundaries = useMemo(
    () =>
      (shapes.polygons || [])
        .filter((poly) => poly.plane_side === PlaneSide.UPPER)
        .map((poly) => ({
          polygon: poly,
          boundary: minAreaRectangleOfPolygon(poly.vertices, poly.positions),
        }))
        .filter((bundle) => bundle?.boundary) as { polygon: Polygon; boundary: MinimumAreaBoundary }[],
    [shapes],
  )

  /**
   * From the selected volume, get the top plane and calculate default interval for both axis (long and short)
   */
  useEffect(
    () => {
      if (!isToolSelected) return

      if (selectedVolumeItem?.inspection_item_id) {
        dispatch(setSelectedVolumeId(selectedVolumeItem.inspection_item_id))

        // If the config has been defined for this volume, skip.
        if (intervals[selectedVolumeItem.inspection_item_id]) return

        const grid = grids.find((row) => row.volume_id === selectedVolumeItem.inspection_item_id)
        let topShapeId = ''
        let bottomShapeId = ''
        let longAxisInt = 0
        let shortAxisInt = 0
        let angle = 0

        const topPolygon = shapes.polygons.find(
          (polygon) =>
            polygon.plane_side === PlaneSide.UPPER && selectedVolumeItem.shape_ids.polygons.includes(polygon.shape_id),
        )
        const bottomPolygon = shapes.polygons.find(
          (polygon) =>
            polygon.plane_side === PlaneSide.LOWER && selectedVolumeItem.shape_ids.polygons.includes(polygon.shape_id),
        )
        const topBoundary = minimumRectangleBoundaries.find(
          (bundle) => bundle.polygon.shape_id === topPolygon?.shape_id,
        )?.boundary

        if (topPolygon && bottomPolygon && topBoundary) {
          const alignedTopBoundary = alignMinimumAreaBoundary(topBoundary, topPolygon, bottomPolygon)
          topShapeId = topPolygon.shape_id || ''
          const [width, height] = topBoundary.extent
          longAxisInt = Math.max(width, height)
          shortAxisInt = Math.min(width, height)
          angle =
            alignedTopBoundary.boundaryAxisAlignment.x.angle *
            (alignedTopBoundary.mirrored &&
            (alignedTopBoundary.mirrored.horizontally || alignedTopBoundary.mirrored.vertically)
              ? 1
              : -1)
        }

        bottomShapeId = bottomPolygon?.shape_id || ''

        if (!topShapeId || !bottomShapeId) return

        // set default values
        let interval: IntervalsConfig = {
          shapeKey: ShapeKeyType.POLYGON,
          locked: false,
          topPlaneId: topShapeId,
          bottomPlaneId: bottomShapeId,
          longAxis: {
            total: meterToMillimeter(longAxisInt),
            interval: {
              min: GRID_POINT_MINIMUM_INTERVAL,
              max: Math.max(Math.round(meterToMillimeter(longAxisInt / 2)), GRID_POINT_MINIMUM_INTERVAL),
              value: Math.max(
                Math.round((meterToMillimeter(longAxisInt / 2) + GRID_POINT_MINIMUM_INTERVAL) / 2),
                GRID_POINT_MINIMUM_INTERVAL,
              ),
            },
            edgeDistance: {
              min: 0,
              max: Math.floor(meterToMillimeter(longAxisInt) / 2),
              value: GRID_EDGE_DEFAULT_EDGE_DISTANCE,
            },
            pointCount: {
              min: GRID_EDGE_MINIMUM_POINT,
              max: calculateMaxPointCount(meterToMillimeter(longAxisInt), GRID_EDGE_DEFAULT_EDGE_DISTANCE),
              value: GRID_EDGE_DEFAULT_POINT,
            },
            // Polygon are origin at the center
            offset: Math.round(meterToMillimeter(longAxisInt / 2)),
          },
          shortAxis: {
            total: meterToMillimeter(shortAxisInt),
            interval: {
              min: GRID_POINT_MINIMUM_INTERVAL,
              max: Math.max(Math.round(meterToMillimeter(shortAxisInt / 2)), GRID_POINT_MINIMUM_INTERVAL),
              value: Math.max(
                Math.round((meterToMillimeter(shortAxisInt / 2) + GRID_POINT_MINIMUM_INTERVAL) / 2),
                GRID_POINT_MINIMUM_INTERVAL,
              ),
            },
            edgeDistance: {
              min: 0,
              max: Math.floor(meterToMillimeter(shortAxisInt) / 2),
              value: GRID_EDGE_DEFAULT_EDGE_DISTANCE,
            },
            pointCount: {
              min: GRID_EDGE_MINIMUM_POINT,
              max: calculateMaxPointCount(meterToMillimeter(shortAxisInt), GRID_EDGE_DEFAULT_EDGE_DISTANCE),
              value: GRID_EDGE_DEFAULT_POINT,
            },
            offset: Math.round(meterToMillimeter(shortAxisInt / 2)),
          },
          angle,
          orderDirection: GridOrderDirection.Horizontal,
          type: GridType.CenterBased,
        } as IntervalsConfig

        // Override with existing grid data
        if (grid?.grid) {
          interval = mergeIntervalsWithSavedConfig(interval, grid.grid)
        } else {
          // Brand-new grid (no saved config): re-apply the user's last-used spacing preferences,
          // clamped to this plane's limits.
          interval = applyGridIntervalPrefs(interval)
        }

        dispatch(
          updateInterval({
            id: selectedVolumeItem.inspection_item_id,
            interval: { ...interval, locked: checkIsLocked(grid?.grid, interval, topShapeId) },
          }),
        )
        dispatch(setHidePlaneLabel(true))

        // For existing grid, set the working grid points on the grid points
        if (grid?.grid) {
          dispatch(
            updateWorkingGridPoints({
              volumeId: selectedVolumeItem.inspection_item_id,
              points: grid.grid.list_distances.map((distance) => ({
                id: crypto.randomUUID(),
                points: [distance.position_on_grid_point, distance.position_on_projected_point],
              })),
            }),
          )
        }
      } else {
        dispatch(setSelectedVolumeId(''))
      }
    },
    // Ignore `intervals` since we don't want to run this effect when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isToolSelected, selectedVolumeItem, grids, shapes.polygons, checkIsLocked, dispatch],
  )

  /**
   * Show attention text
   */
  useEffect(() => {
    if (!isToolSelected) return

    dispatch(
      setAttentionText({
        message: isPlaneSelected
          ? [
              'グリッド間隔は右側のパネルから変更できます。',
              '各点はドラッグして移動することができます。',
              '選択が完了したら右下の測定ボタンをクリックしてください。',
            ].join('\n')
          : '平面をクリックして領域を選択してください。',
      }),
    )
  }, [isToolSelected, isPlaneSelected, dispatch])

  /**
   * Reset store on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  /**
   * Run delete of grid depth data
   */
  const handleSave = useCallback(async () => {
    if (!Object.keys(intervals).length || !inspectionSheet || !project_id || !inspection_area_id) {
      return
    }

    await withPromiseContained(async () => {
      const access_token = await getAccessToken()
      if (!access_token) {
        return
      }

      dispatch(addWorkingTool(EDITOR_TOOLS.GRID))
      dispatch(setIsLoading(true))
      changeIsJobRunning(true)

      // Start estimating depth
      Object.values(intervals).forEach((interval) => {
        mixpanel.track('Create depth grid', {
          'Grid Count': 1,
          'Grid Type': interval.type,
          'Long Axis Interval (mm)': interval.longAxis.interval.value,
          'Short Axis Interval (mm)': interval.shortAxis.interval.value,
          'Long Axis Length (mm)': interval.longAxis.total,
          'Short Axis Length (mm)': interval.shortAxis.total,
        })
      })
      const jobTokens = await Promise.all(
        Object.keys(intervals)
          .filter((volumeId) => workingGridPoints[volumeId])
          .map((volumeId) =>
            estimateGridDepth(
              access_token,
              project_id,
              inspection_area_id,
              inspectionSheet.inspection_sheet_id,
              `測定したグリッド ${grids.length + 1}`,
              volumeId,
              intervals[volumeId],
              intervals[volumeId].shapeKey === ShapeKeyType.POLYGON
                ? [intervals[volumeId].topPlaneId, intervals[volumeId].bottomPlaneId]
                : undefined,
              workingGridPoints[volumeId].filter(({ points }) => points[0] && points[1]).map(({ points }) => points[0]),
              showErrorModal,
            ),
          ),
      )

      // Refresh inspection items to get the updated data
      if (jobTokens.some((result) => result)) {
        // Run poll to check for job completion
        const results = await Promise.all(
          (jobTokens.filter((jobToken) => jobToken) as string[]).map((jobToken) =>
            pollAsyncJobStatus<InspectionItem>(access_token, jobToken),
          ),
        )

        if (results.some((result) => result)) {
          await fetchInspectionItems()
          dispatch(resetWorkingGrid())

          if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
          successToastIdRef.current = toaster.create({
            title: 'グリッド測定が完了しました',
            type: 'success',
            duration: 3000,
            closable: true,
          })
        }
      }
    })
      .catch(() => {
        showErrorModal('グリッド測定中にエラーが発生しました。時間をおいて再度お試しください。')
      })
      .finally(() => {
        dispatch(setIsLoading(false))
        dispatch(setAttentionText({ message: '' }))
        dispatch(removeWorkingTool(EDITOR_TOOLS.GRID))
        changeIsJobRunning(false)
      })
  }, [
    intervals,
    project_id,
    inspection_area_id,
    inspectionSheet,
    grids,
    workingGridPoints,
    dispatch,
    getAccessToken,
    showErrorModal,
    changeIsJobRunning,
    fetchInspectionItems,
  ])

  /**
   * Handle apply operation.
   */
  const handleApply = useCallback(async () => {
    dispatch(setAttentionText({ message: 'グリッドを測定中...' }))
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }, [dispatch, handleSave])

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = useCallback(() => {
    dispatch(setAttentionText({ message: '' }))

    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, true)

    if (isSaveable) {
      if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
      infoToastIdRef.current = toaster.create({
        title: 'グリッド測定を開始しました',
        description:
          'グリッド測定が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }, [changeTool, handleSave, dispatch, isSaveable])

  const isEveryIntervalInRange = useMemo(
    () => Object.keys(intervals).every((volumeId) => validateIntervalConfig(intervals[volumeId])),
    [intervals],
  )

  return {
    buttons: {
      apply: {
        key: 'estimate-depth-grid-apply',
        label: `測定(${Object.keys(workingGridPoints).length}個のグリッド)`,
        loadingLabel: '測定中',
        tooltip: !isEveryIntervalInRange ? (
          <Text color="orange.400">グリッド間隔が正しく設定されていません</Text>
        ) : undefined,
        onClick: handleApply,
        isShown: () => isToolSelected,
        isLoading: () => isLoading,
        isDisabled: () => !isSaveable || !isEveryIntervalInRange,
      },
      submit: {
        key: 'estimate-depth-grid-submit',
        label: isDirty ? '測定して終了' : undefined,
        loadingLabel: '測定中',
        isLoading: () => false,
        isShown: () => isToolSelected,
        isDisabled: () => false,
        onClick: handleSubmit,
      },
      reset: {
        onClick: () => {
          clearGridIntervalPrefs()
          dispatch(resetWorkingGrid())
          dispatch(setSelectedElementIds([]))
        },
        isShown: () => isToolSelected,
        isDisabled: () => !isSaveable,
      },
    },
  }
}

export default useEditor
